import {observable, computed, action} from 'mobx';
import {getIn} from '../utils';
import * as constants from './const';
import createSelector, {LruCacheObject} from 're-reselect';

class PaymentType {
  constructor(content) {
    this.content = content;
  }

  @computed get id() {
    return getIn(this.content, ['Id']);
  }

  @computed get name() {
    return getIn(this.content, ['Name']);
  }

  @computed get description() {
    return getIn(this.content, ['Description']);
  }
}

class CancellationFee {
  constructor(content) {
    this.content = content;
  }

  @computed get minHoursPriorToPickup() {
    let value = getIn(this.content, ['MinHoursPriorToPickup'])
    return parseInt(value, 10);
  }

  @computed get rate() {
    return getIn(this.content, ['Rate']);
  }

  @computed get fix() {
    return getIn(this.content, ['Fix']);
  }
}

class BusinessUnitAddress {
  @observable content = {};

  constructor(content) {
    this.content = content;
  }

  @computed get addressType() {
    return this.content.refAddressTypeId;
  }

  @computed get street() {
    return this.content.Street;
  }

  @computed get addition() {
    return this.content.AddressAddition;
  }

  @computed get postalCode() {
    return this.content.PostalCode;
  }

  @computed get city() {
    return this.content.City;
  }

  @computed get country() {
    return this.content.refCountryId;
  }
}

export class BusinessUnit { 
  @observable content = {};

  @computed get canShowAddress() {
    return this.name && this.postAddress.street && this.postAddress.city;
  }

  @action.bound clear() {
    this.content = {};
  }

  constructor(api, caOrgId) {
    this.api = api;
    this.caOrgId = caOrgId;
  }

  @computed get services() {
    return getIn(this.content, ['GeneralRentalService'], []);
  }

  @computed get paymentTypes() {
    return getIn(this.content, ['BusinessUnitPaymentMethod'], []).map(o => {
      return new PaymentType(o);
    });
  }

  @computed get bankAccountDescription() {
    return getIn(this.content, ['BankAccountDescription'])
  }

  @computed get bankAccountPaymentInstruction() {
    return getIn(this.content, ['BankAccountPaymentInstruction'])
  }

  @computed get cancellationFees() {
    var compareFn = function(a, b) {
      return a.MinHoursPriorToPickup - b.MinHoursPriorToPickup;
    }
    return getIn(this.content, ['BusinessUnitCancellationFee'], []).sort(compareFn).map(o => {
      return new CancellationFee(o);
    })
  }

  @computed get id() {
    return getIn(this.content, ['Id']);
  }

  @computed get name() {
    return getIn(this.content, ['Name']);
  }

  @computed get currencyName() {
    return getIn(this.content, ['Currency_Name']);
  }

  @computed get addresses() {
    return getIn(this.content, ['BusinessUnitAddress'], []).map(o => new BusinessUnitAddress(o));
  }

  @computed get postAddress() {
    return this.addresses.find(o => o.addressType === constants.ADDRESS_TYPE.POST) || {};
  }

  @computed get lat() {
    return getIn(this.content, ['Latitude']);
  }

  @computed get lon() {
    return getIn(this.content, ['Longitude']);
  }  

  @computed get reservationAllowed() {
    return getIn(this.content, ['IsReservationAllowed']);
  }

  @computed get reservationExpiration() {
    return getIn(this.content, ['ReservationExpiration']);
  }

  @computed get phone() {
    return getIn(this.content, ['Phone']);
  }

  @computed get email() {
    return getIn(this.content, ['Email']);
  }  

  @computed get loginMessage() {
    return getIn(this.content, ['LoginMessage']);
  }    

  @action async fetch({organizationId, businessUnitId}) {
    if(!businessUnitId) {
      this.content = {};
      return;
    }
    if(this._loadingPromise) {
      return this._loadingPromise;
    }
    if(businessUnitId !== this.id) {
      try {    
        this._loadingPromise = this.api.getBusinessUnit({
          organizationId,
          businessUnitId,
        });
        this.content = await this._loadingPromise;
      }
      finally {
        this._loadingPromise = undefined;
      }
    }
  }

  cFetch = createSelector(
    [p => p],
    (p) => this.fetch(p)
  )(
    ({organizationId, businessUnitId}) => 
      `${organizationId}@${businessUnitId}`,
    {cacheObject: new LruCacheObject({cacheSize: 1}), }
  );  
}


// WEBPACK FOOTER //
// ./src/model/BusinessUnit.js